import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
  FormControl,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";

const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable } = useLocationHelpers();

  /* ===========================
      STATE
  ============================ */

  const [loadingOptions, setLoadingOptions] = useState(false);

  const [options, setOptions] = useState({
    companies: [],
    provinces: [],
    groups: [],
    notifications: [],
    agreementSigned: [],
  });

  const [associationData, setAssociationData] = useState({
    company_id: null,
    asoc_name: "",
    asoc_grupo: null,
    asoc_notifications: null,
    asoc_contactname: "",
    asoc_cif: "",
    asoc_address: "",
    asoc_province: null,
    asoc_postalcode: "",
    asoc_email: "",
    asoc_acuerdo: null,
    asoc_phone: "",
  });

  const [associationObservations, setAssociationObservations] = useState("");
  const [marketingToSend, setMarketingToSend] = useState(0);

  /* ===========================
      FETCH INITIAL DATA
  ============================ */

  const getData = async () => {
    try {
      setLoadingOptions(true);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/create`,
      );

      setOptions({
        companies: [{ company_name: "Seleccionar", id: 0 }, ...data.companies],
        provinces: [{ provincia: "Seleccionar", id: 0 }, ...data.provinces],
        groups: [{ grupo: "Seleccionar", id: 0 }, ...data.groups],
        notifications: [
          { nombre: "Seleccionar", id: 0 },
          { nombre: "Si", id: 1 },
          { nombre: "No", id: 0 },
        ],
        agreementSigned: [
          { nombre: "Seleccionar", id: 0 },
          { nombre: "Si", id: 1 },
          { nombre: "No", id: 0 },
        ],
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* ===========================
      HANDLERS
  ============================ */

  const handleChange = ({ target }) => {
    setAssociationData((prev) => ({
      ...prev,
      [target.name]: target.value.trim(),
    }));
  };

  const handleAutocompleteChange = (_, value, fieldName) => {
    setAssociationData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /* ===========================
      FORM CONFIG
  ============================ */

  const formFields = [
    {
      label: "Empresa",
      name: "company_id",
      type: "autocomplete",
      options: options.companies,
      getOptionLabel: (o) => o.company_name || "",
      isOptionEqualToValue: (o, v) => o.id === v.id,
    },
    {
      label: "Provincia",
      name: "asoc_province",
      type: "autocomplete",
      options: options.provinces,
      getOptionLabel: (o) => o.provincia || "",
      isOptionEqualToValue: (o, v) => o.provincia_id === v.provincia_id,
    },
    {
      label: "Grupos",
      name: "asoc_grupo",
      type: "autocomplete",
      options: options.groups,
      getOptionLabel: (o) => o.grupo || "",
      isOptionEqualToValue: (o, v) => o.id === v.id,
    },
    {
      label: "¿Notificaciones?",
      name: "asoc_notifications",
      type: "autocomplete",
      options: options.notifications,
      getOptionLabel: (o) => o.nombre || "",
      isOptionEqualToValue: (o, v) => o.id === v.id,
    },
    {
      label: "¿Acuerdo firmado?",
      name: "asoc_acuerdo",
      type: "autocomplete",
      options: options.agreementSigned,
      getOptionLabel: (o) => o.nombre || "",
      isOptionEqualToValue: (o, v) => o.id === v.id,
    },
    { name: "asoc_name", label: "Nombre", type: "text" },
    { name: "asoc_phone", label: "Teléfono", type: "text", length: 15 },
    {
      name: "asoc_contactname",
      label: "Persona de contacto",
      type: "text",
    },
    { name: "asoc_cif", label: "CIF", type: "text", length: 15 },
    { name: "asoc_address", label: "Dirección", type: "text" },
    {
      name: "asoc_postalcode",
      label: "Código postal",
      type: "text",
      length: 15,
    },
    { name: "asoc_email", label: "Email", type: "text" },
    {
      name: "asoc_observaciones",
      label: "Observaciones",
      type: "textarea",
      required: false,
    },
  ];

  /* ===========================
      VALIDATION
  ============================ */

  const fieldLabelMap = Object.fromEntries(
    formFields.map((f) => [f.name, f.label]),
  );

  const requiredFields = formFields
    .filter((f) => f.required !== false)
    .map((f) => f.name);

  const validateFields = () =>
    requiredFields.every(
      (field) =>
        associationData[field] !== "" && associationData[field] !== null,
    );

  const showValidationErrors = () => {
    const missingFields = requiredFields
      .filter(
        (field) =>
          associationData[field] === "" || associationData[field] === null,
      )
      .map((field) => `<li>${fieldLabelMap[field]}</li>`)
      .join("");

    Swal.fire({
      icon: "error",
      title: "Campos obligatorios",
      html: `<div style="text-align:left">
        Complete los siguientes campos:
        <ul>${missingFields}</ul>
      </div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  /* ===========================
      FORM DATA
  ============================ */

  const buildFormData = () => {
    const formData = new FormData();

    Object.entries(associationData).forEach(([key, value]) => {
      if (value && typeof value === "object") {
        formData.append(key, value.id ?? "");
      } else {
        formData.append(key, value ?? "");
      }
    });

    formData.append(
      "asoc_observaciones",
      associationObservations.replace(/\n/g, "<br />"),
    );

    formData.append("marketing", marketingToSend ? 1 : 0);

    return formData;
  };

  /* ===========================
      API
  ============================ */

  const createAssociation = async (formData) => {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/associations/store`,
      formData,
    );
    return data;
  };

  /* ===========================
      SUBMIT
  ============================ */

  const handleAssociationsFormInformation = async () => {
    if (!validateFields()) {
      showValidationErrors();
      return;
    }

    const confirm = await Swal.fire({
      title: "¿Desea guardar los cambios?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Volver",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Guardando asociación",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const data = await createAssociation(buildFormData());

      console.log(data);

      Swal.close();

      const associationId = data?.association_created?.id;

      const success = await Swal.fire({
        icon: "success",
        title: "Asociación creada",
        confirmButtonText: "Ver asociación",
        confirmButtonColor: "#1ab394",
      });

      if (success.isConfirmed && associationId) {
        navigate(`/asociaciones/ver?id=${associationId}`);
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo guardar la información", "error");
    }
  };

  /* ===========================
      RENDER
  ============================ */

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Asociaciones" subTitle="Crear" />

      <Box component="form" className="formMainContainer" noValidate>
        <Grid container p={2}>
          {formFields.map((field, idx) =>
            field.type === "autocomplete" ? (
              <Grid item xs={12} sm={6} key={idx} p={1}>
                <Autocomplete
                  value={associationData[field.name]}
                  options={field.options}
                  loading={loadingOptions}
                  size="small"
                  getOptionLabel={field.getOptionLabel}
                  isOptionEqualToValue={field.isOptionEqualToValue}
                  onChange={(e, v) =>
                    handleAutocompleteChange(e, v, field.name)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={field.label} fullWidth />
                  )}
                />
              </Grid>
            ) : field.type === "textarea" ? (
              <Grid item xs={12} key={idx} p={1}>
                <FormControl fullWidth>
                  <FormLabel>{field.label}</FormLabel>
                  <textarea
                    style={{
                      height: 100,
                      resize: "none",
                      padding: 10,
                      borderRadius: 4,
                      border: "1px solid rgba(0,0,0,0.3)",
                    }}
                    onChange={(e) => setAssociationObservations(e.target.value)}
                  />
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} key={idx} p={1}>
                <TextField
                  name={field.name}
                  label={field.label}
                  value={associationData[field.name]}
                  inputProps={{ maxLength: field.length }}
                  onChange={handleChange}
                  size="small"
                  fullWidth
                />
              </Grid>
            ),
          )}

          <Grid item xs={12} mt={2}>
            <ButtonGroup fullWidth>
              <Button
                color="success"
                onClick={handleAssociationsFormInformation}>
                Guardar
              </Button>
              <Button color="error" onClick={() => goTable("asociaciones")}>
                Tabla
              </Button>
              <Button onClick={goPanel}>Inicio</Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default Crear;
